import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import type { StrictEffect } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import type { AxiosResponse } from 'axios';
import actions, { actionTypes, selectors } from '../reducers/BarcodeAction';
import machineActions, {
  actionTypes as machineActionTypes,
  selectors as machineSelectors,
} from '../reducers/Machine';
import WebRequest from './WebRequest';
import { barcodeResolve } from 'js/api/BarcodeActions';
import {
  AMOUNT_CARD,
  BARCODE_ACTIONS_IN_MODE,
  BARCODE_BESPOKE_ADLER,
  BARCODE_CANISTER,
  BARCODE_COLOR,
  BARCODE_COLORANT,
  BARCODE_FORMULA,
  BARCODE_MODE_DISPENSE,
  BARCODE_PREFIXCOLOURCODE,
  BARCODE_PREFIXORDERLOAD,
  BARCODE_PREFIXORDERLOADBYITEM,
  BARCODE_PRODUCT,
  BARCODE_VALIDATION_CONFIRM,
  BARCODE_VALIDATION_ERROR,
  BARCODE_VALIDATION_OK,
  COLOR_SEARCH_CARD,
  INFO_CARD,
  ORDER_MODE_NORMAL,
  PRODUCT_SEARCH_CARD,
} from '../../Constants';
import busyActions from '../reducers/Busy';
import formulaActions from '../reducers/Formula';
import _ from 'lodash';
import orderActions, {
  actionTypes as orderActionTypes,
  selectors as orderSelectors,
  RETINT_TYPE_ORDER,
} from '../reducers/Order';
import { selectors as cacheSelectors } from '../reducers/Cache';
import { selectors as configSelectors } from '../reducers/Configuration';
import { selectors as userSelectors } from '../reducers/User';
import { push } from 'js/redux/Router';
import i18n from '../../localization/i18n';
import log from '../../api/Logger';
import {
  buildAdlerOrderitem,
  parseAdlerQrPayload,
} from '../../mylib/customer/AdlerQrInput';
import { setOrderitem } from './OrderFetching';
import {
  applyLoadedFormula,
  fetchFormula,
  type LoadedFormula,
} from './Formula';
import { waitForColorants } from './Cache';
import { waitForSite, waitForZone } from './Configuration';
import type { Colorant } from 'js/api/types/Cache';
import type { DispenserId, MachineCanister } from 'js/api/types/Machine';
import type { RootState } from '../reducers';
import type {
  BarcodeAction,
  BarcodeActionError,
  BarcodeActionResult,
  BarcodeAdlerAction,
  BarcodeCanisterAction,
  BarcodeColorantAction,
  BarcodeMode,
  BarcodeResolveResponse,
} from '../types/BarcodeAction';
import type { CacheProduct } from '../types/Cache';
import type { OrderAction, OrderState } from '../types/Order';
import { wrongBasePaintError } from './BarcodeValidation';

type ResolveBarcodeAction = BarcodeAction<string> & { payload: string };
type DelegatedSagaIterator<Return = void> = Generator<StrictEffect, Return>;

function selectPathname(state: RootState): string {
  return (state.router.location as unknown as { pathname: string }).pathname;
}

function isCacheProduct(
  product: OrderState['product']
): product is CacheProduct {
  return (
    product != null &&
    'productid' in product &&
    product.productid != null &&
    Array.isArray(product.basepaints)
  );
}

function* clearScannedBaseOnProductChange(
  action: OrderAction<OrderState['product']>
): DelegatedSagaIterator<void> {
  const lastScannedBaseid: number | null = yield select(
    selectors.last_scanned_baseid
  );
  const product = action.payload ?? null;
  const keepsScannedBase =
    isCacheProduct(product) &&
    product.basepaints.some((base) => base?.baseid === lastScannedBaseid);

  if (action.meta?.source !== 'barcode' && !keepsScannedBase) {
    yield put(actions.clearLastScannedProductIds());
  }
}

function* resolveCanisterBarcode(
  barcode: string
): DelegatedSagaIterator<BarcodeCanisterAction | null> {
  const [dispID, canister]: [DispenserId, MachineCanister] | [] = yield select(
    machineSelectors.find_dispID_canister,
    barcode
  );
  if (canister && dispID !== undefined) {
    return {
      action: BARCODE_CANISTER,
      dispID,
      canisterid: canister.id,
      cntcode: canister.code,
      barcode,
      canister,
    };
  }
  return null;
}

function* resolveColorantBarcode(
  barcode: string
): DelegatedSagaIterator<BarcodeColorantAction | null> {
  const cnts: Colorant[] = yield select(cacheSelectors.cnts);
  for (let cnt of cnts) {
    for (let cntcan of cnt.cntcans) {
      if (cntcan.barcodes.includes(barcode)) {
        return {
          action: BARCODE_COLORANT,
          cntid: cnt.cntid,
          cansizeid: cntcan.cansizeid,
          cntcanid: cntcan.cntcanid,
          barcode,
        };
      }
    }
  }
  return null;
}

function resolveAdlerQrBarcode(barcode: string): BarcodeAdlerAction | null {
  const adlerQr = parseAdlerQrPayload(barcode);
  if (!adlerQr) return null;

  return {
    action: BARCODE_BESPOKE_ADLER,
    barcode: adlerQr.originalPayload,
    adlerQr,
  };
}

function* handleAdlerQrBarcode(
  ba: BarcodeAdlerAction
): DelegatedSagaIterator<void> {
  yield call(waitForColorants);
  const cntcodemap = yield select(cacheSelectors.cntcodemap);
  const products = yield select(cacheSelectors.products);
  const cansizes = yield select(cacheSelectors.cansizes);
  const { zoneid } = yield call(waitForZone);
  const { siteid } = yield call(waitForSite);
  const orderitem = buildAdlerOrderitem(
    ba.adlerQr,
    cntcodemap,
    products,
    cansizes,
    zoneid,
    siteid
  );

  yield* setOrderitem(
    orderitem as unknown as Parameters<typeof setOrderitem>[0],
    RETINT_TYPE_ORDER,
    false
  );
  yield put(orderActions.setSourceBarcode(ba.barcode));
  const unitname = ba.adlerQr.components[1]?.unit; // first colorant component unit, if any
  if (unitname) {
    yield put(orderActions.setSelectedUnit(unitname));
  }
}

function* resolveBarcode(action: ResolveBarcodeAction): SagaIterator<void> {
  const barcode = action.payload;
  yield put(busyActions.setBusy(true));
  const mode: BarcodeMode = yield select(selectors.barcodemode);
  yield put(actions.resolveBarcodePending(true));

  try {
    yield put({ type: actionTypes.SETBARCODE, payload: barcode });

    // check local barcodes before calling server
    let ba: BarcodeActionResult | null =
      resolveAdlerQrBarcode(barcode) ||
      (yield* resolveCanisterBarcode(barcode)) ||
      (yield* resolveColorantBarcode(barcode));
    if (!ba) {
      const response: AxiosResponse<BarcodeResolveResponse> = yield call(
        WebRequest,
        barcodeResolve(barcode)
      );
      ba = response.data;
    }
    /**
     * Handling action barcodes
            "R": "actionRetry",
            "K": "actionOk",
            "I": "actionSkip",
            "A": "actionCancel",
            "U": "actionContinue",
            "O": "actionCreateNew",
            "D": "actionDispense",
            "S": "actionSave",
            "T": "actionSaveAndDispense",
     */
    if (ba.action && ba.action.startsWith('action')) {
      // Only trigger actions valid for mode
      if (_.includes(BARCODE_ACTIONS_IN_MODE[mode], ba.action)) {
        // Trying to fire that action in UI by clicking the item having the id
        const elm = document.getElementById(ba.action);
        if (elm) {
          elm.click();
        }
      }
    } else {
      // send returned object back to reducer as payload:
      yield put(actions.resolveBarcodeFulfilled(ba));

      if (mode === BARCODE_MODE_DISPENSE) {
        /**
         * Dispensing and need to validate can barcode
         */
        yield* handleCanValidation(ba);
      } else {
        if (
          // In case of product, color or formula barcode search rest!
          ba.action === BARCODE_PRODUCT ||
          ba.action === BARCODE_COLOR ||
          ba.action === BARCODE_FORMULA ||
          ba.action === BARCODE_PREFIXCOLOURCODE
        ) {
          yield* handleProductColorFormulaBarcode(ba);
        } else if (ba.action === BARCODE_COLORANT) {
          yield* handleColorantBarcode(barcode, ba);
        } else if (ba.action === BARCODE_PREFIXORDERLOAD) {
          // Loading one item of the order
          yield put(orderActions.fetchOrderitem(ba.itemid!, false, ba.zoneid));
        } else if (ba.action === BARCODE_PREFIXORDERLOADBYITEM) {
          yield put(orderActions.fetchOrderitem(ba.itemid!, false, ba.zoneid));
        } else if (ba.action === BARCODE_CANISTER) {
          yield* handleCanisterBarcode(ba);
        } else if (ba.action === BARCODE_BESPOKE_ADLER) {
          yield* handleAdlerQrBarcode(ba);
        } else {
          yield put(
            actions.resolveBarcodeRejected({
              msg: i18n.t(
                'msg.barcodeNotValid',
                'Barcode not valid, please check it'
              ),
            })
          );
        }
      }
    }
  } catch (e) {
    log.error(e);
    yield put(actions.resolveBarcodeRejected(barcodeResolveErrorPayload(e)));
  } finally {
    yield put(actions.resolveBarcodePending(false));
    yield put(busyActions.setBusy(false));
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function barcodeResolveErrorPayload(error: unknown): BarcodeActionError {
  if (isRecord(error) && typeof error.msg === 'string' && error.msg) {
    return {
      msg: error.msg,
      ...(typeof error.title === 'string' ? { title: error.title } : {}),
    };
  }
  const response = isRecord(error) ? error.response : undefined;
  const data = isRecord(response) ? response.data : undefined;
  const dataMessage = isRecord(data) ? data.msg : undefined;
  const errorMessage = isRecord(error) ? error.message : undefined;
  const msg =
    (typeof data === 'string'
      ? data
      : typeof dataMessage === 'string'
        ? dataMessage
        : undefined) ||
    (typeof errorMessage === 'string' ? errorMessage : undefined) ||
    i18n.t(
      'msg.barcodeActionUnexpectedError',
      'Barcode action failed, please try again'
    ) ||
    'Barcode action failed, please try again';

  return { msg };
}

function* handleCanValidation(
  ba: BarcodeActionResult
): DelegatedSagaIterator<void> {
  const { barcode_can_check_before_dispensing_confirmation } = yield select(
    configSelectors.config_values
  );
  const can = yield select(orderSelectors.can);
  const valid =
    (ba.action === BARCODE_PRODUCT || ba.action === BARCODE_FORMULA) &&
    can &&
    ba.canids.includes(can.canid);
  yield put(
    actions.setValidationState(
      valid
        ? barcode_can_check_before_dispensing_confirmation
          ? BARCODE_VALIDATION_CONFIRM
          : BARCODE_VALIDATION_OK
        : BARCODE_VALIDATION_ERROR
    )
  );
}

function* handleProductColorFormulaBarcode(
  ba: BarcodeResolveResponse
): DelegatedSagaIterator<void> {
  let color: OrderState['color'] = yield select(orderSelectors.color);
  let product: OrderState['product'] = yield select(orderSelectors.product);
  let base: OrderState['base'] = yield select(orderSelectors.base);
  let can: OrderState['can'] = yield select(orderSelectors.can);
  const lastScannedBaseid: number | null = yield select(
    selectors.last_scanned_baseid
  );
  const changesProduct =
    ba.action === BARCODE_PRODUCT || ba.action === BARCODE_FORMULA;
  const changesColor =
    ba.action === BARCODE_COLOR ||
    ba.action === BARCODE_FORMULA ||
    ba.action === BARCODE_PREFIXCOLOURCODE;

  if (changesProduct) {
    const scannedProduct: CacheProduct | undefined =
      ba.productid == null
        ? undefined
        : yield select(cacheSelectors.getProduct, ba.productid);
    product = scannedProduct ?? null;
    if (scannedProduct) {
      base =
        _.find(
          scannedProduct.basepaints,
          (candidate) => candidate.baseid === ba.baseid
        ) ?? null;
      if (base) {
        can = _.find(base.cans, (x) => x.canid === ba.canid) ?? null;
      }
    } else {
      base = null;
      can = null;
    }
  }

  if (changesColor) {
    color = {
      ..._.pick(ba, 'rgb', 'colourid'),
      colourcode: ba.colourcode ?? undefined,
    };
  }

  if (color?.colourid && isCacheProduct(product) && product.productid) {
    const formulaPayload = {
      colourid: color.colourid,
      productid: product.productid,
      can,
    };
    const loadedFormula: LoadedFormula = yield call(
      fetchFormula,
      formulaPayload
    );

    if (loadedFormula.selectedFormula) {
      const selectedFormula = loadedFormula.selectedFormula;
      const formulaBase = product.basepaints.find(
        (b) => b.baseid === selectedFormula.baseid
      );
      if (!base && formulaBase) {
        base = formulaBase;
      }
      // In case that we have scanned some canid with last product
      if (base && !can) {
        const canid = changesProduct
          ? ba.canid
          : yield select(selectors.last_scanned_canid);
        can = base.cans.find((c) => c.canid === canid) ?? null;
      }

      const expectedBaseid = changesProduct
        ? ba.baseid
        : (lastScannedBaseid ?? base?.baseid);
      if (base && selectedFormula.baseid === expectedBaseid) {
        if (changesProduct) {
          yield put(orderActions.setProduct(product, 'barcode'));
          yield put(actions.setLastScannedProductIds(ba.baseid, ba.canid));
          yield put(orderActions.setBase(base));
        }
        if (changesColor) {
          yield put(orderActions.setColor(color));
        }
        yield put(orderActions.setOrderMode(ORDER_MODE_NORMAL));
        yield call(applyLoadedFormula, formulaPayload, loadedFormula);
        if (can) {
          yield put(orderActions.setCan(can));
          yield put(orderActions.setOpenSection(INFO_CARD));
          const { barcode_formula_auto_tint } = yield select(
            configSelectors.config_values
          );
          if (barcode_formula_auto_tint) {
            yield put(orderActions.initiateTinting());
          }
        } else {
          yield put(orderActions.setOpenSection(AMOUNT_CARD));
        }
      } else {
        const basecode = formulaBase?.basecode ?? '...'; // fallback not needed in normal scenarios
        yield put(
          actions.resolveBarcodeRejected(wrongBasePaintError(basecode))
        );

        if (changesColor) {
          yield put(orderActions.setColor(color));
        }
        yield put(orderActions.setProduct(null));
        // Move color first
        yield put(
          orderActions.setOrderMode(ORDER_MODE_NORMAL, COLOR_SEARCH_CARD)
        );
        yield put(orderActions.moveToNextSection(COLOR_SEARCH_CARD));
      }
    } else {
      // No formula for this pair! Notify user
      yield put(
        actions.resolveBarcodeRejected({
          title: i18n.t(
            'msg.barcodeValidNoFormulaForPair.title',
            'Formula not found'
          ),
          msg: i18n.t(
            'msg.barcodeValidNoFormulaForPair',
            'Barcode is valid barcode, but formula for selected product and color code does not exist'
          ),
        })
      );
    }
  } else {
    if (changesProduct) {
      yield put(orderActions.setProduct(product, 'barcode'));
      yield put(actions.setLastScannedProductIds(ba.baseid, ba.canid));
    }
    if (changesColor) {
      yield put(orderActions.setColor(color));
    }

    if (color && !product) {
      // Move color first
      yield put(
        orderActions.setOrderMode(ORDER_MODE_NORMAL, COLOR_SEARCH_CARD)
      );
      yield put(orderActions.moveToNextSection(COLOR_SEARCH_CARD));
    } else if (!color && product) {
      // Move product first
      yield put(orderActions.setCan(can));

      yield put(
        orderActions.setOrderMode(ORDER_MODE_NORMAL, PRODUCT_SEARCH_CARD)
      );
      yield put(orderActions.moveToNextSection(PRODUCT_SEARCH_CARD));
    }
  }
  // Navigate to order page if not already there
  yield put(push('/orderpage'));
}

function* handleColorantBarcode(
  barcode: string,
  ba: BarcodeColorantAction | BarcodeResolveResponse
): DelegatedSagaIterator<void> {
  /* {
            action: 'colorant',
            cansizeid: 3,
            cntcanid: 32,
            cntid: 18, ....
          }; */
  // Check barcode refill privilege
  const privileges = yield select(userSelectors.privileges);

  if (!_.includes(privileges, 'canister_level_change')) {
    yield put(
      actions.resolveBarcodeRejected({
        title: i18n.t('lbl.accessDenied'),
        msg: i18n.t(
          'msg.barcodeValidAccessDenied',
          'Barcode valid, access denied'
        ),
      })
    );
    return;
  }

  const [dispID, canisters]: [DispenserId | null, MachineCanister[]] =
    yield select(machineSelectors.find_dispID_canisters, ba.cntid as number);
  let canister = canisters[0];
  if (!canister) {
    return;
  }
  canister = { ...canister, alt_canisters: canisters };

  yield put({
    type: machineActionTypes.SET_MACHINE_BARCODE_REFILL_ACTION,
    payload: {
      cntid: ba.cntid,
      cansizeid: ba.cansizeid,
      barcode,
      canisterid: canister.id,
    },
  });

  const current_target_canister = yield select(
    machineSelectors.refill_target_canister
  );

  if (!current_target_canister) {
    // Refill modal is not open
    yield put({
      type: machineActionTypes.SET_CURRENT_MACHINE,
      payload: { dispID },
    });
    yield put({
      type: machineActionTypes.CMD_MOVE_TO_FILL,
      payload: {
        dispID,
        canid: canister.id,
        code: canister.code,
      },
    });

    // Need to check that if we are ready page --> no need to navigate
    const path: string = yield select(selectPathname);
    if (
      ['/orderpage', '/localformula', '/matching', '/freedispense'].includes(
        path
      )
    ) {
      yield put({
        type: machineActionTypes.SET_MACHINE_SLIDE_OVER_VISIBLE,
        payload: { dispID, visible: true },
      });
    } else {
      yield put(push('/machine'));
    }
    yield put(machineActions.setRefillTargetCanister(canister));
  }
}

function* handleCanisterBarcode(
  ba: BarcodeCanisterAction
): DelegatedSagaIterator<void> {
  /*  ba is like this:
  {
      action: BARCODE_CANISTER,
      dispID,
      canisterid,
      cntcode,
      barcode,
      canister,
    }
  */
  // Check barcode refill privilege
  const privileges = yield select(userSelectors.privileges);

  if (!_.includes(privileges, 'canister_level_change')) {
    yield put(
      actions.resolveBarcodeRejected({
        title: i18n.t('lbl.accessDenied'),
        msg: i18n.t(
          'msg.barcodeValidAccessDenied',
          'Barcode valid, access denied'
        ),
      })
    );
    return;
  }

  yield put({
    type: machineActionTypes.SET_MACHINE_BARCODE_CANISTER_ACTION,
    payload: ba,
  });

  const current_target_canister = yield select(
    machineSelectors.refill_target_canister
  );
  const current_dispID = yield select(machineSelectors.current_dispID);

  if (current_target_canister) {
    // Refill modal is already open
    if (
      current_target_canister.id !== ba.canisterid &&
      current_dispID === ba.dispID &&
      current_target_canister.code === ba.cntcode
    ) {
      // Different canister for same colorant, change canister
      yield put({
        type: machineActionTypes.CMD_MOVE_TO_FILL,
        payload: {
          dispID: ba.dispID,
          canid: ba.canisterid,
          code: ba.cntcode,
        },
      });
      yield put(machineActions.setRefillTargetCanister(ba.canister));
    }
  } else {
    // Refill modal is not open
    yield put({
      type: machineActionTypes.SET_CURRENT_MACHINE,
      payload: { dispID: ba.dispID },
    });

    // Need to check that if we are ready page --> no need to navigate
    const path: string = yield select(selectPathname);
    if (
      ['/orderpage', '/localformula', '/matching', '/freedispense'].includes(
        path
      )
    ) {
      yield put({
        type: machineActionTypes.SET_MACHINE_SLIDE_OVER_VISIBLE,
        payload: { dispID: ba.dispID, visible: true },
      });
    } else {
      yield put(push('/machine'));
    }
    yield put(machineActions.setRefillTargetCanister(ba.canister));
  }
}

export default function* saga(): SagaIterator<void> {
  yield all([
    takeLatest(actionTypes.RESOLVE_BARCODE, resolveBarcode),
    takeEvery(orderActionTypes.SET_PRODUCT, clearScannedBaseOnProductChange),
  ]);
}
